import { HTMLAttributes } from 'react'
import { useMeasure } from 'react-use'
import Link from 'next/link'

import Button from '@/components/base/Button'
import Container from '@/components/base/Container'
import Grid from '@/components/base/Grid'
import Image from '@/components/base/Image'
import MediaParallax from '@/components/base/MediaParallax'
import Motion from '@/components/base/Motion'
import SVGIcon from '@/components/base/SVGIcon'
import Typography from '@/components/base/Typography'

import { imageParagraph } from './animations'
import { cn } from '@/lib/utils'
import { WPImage, WPLink, WPPageData, WPVideo } from '@/types'

export type ImageParagraphProps = HTMLAttributes<HTMLElement> &
  Partial<{
    paragraph: {
      title: string
      subtitle: string
      paragraph: string
      button: WPLink
    }
    button: WPLink
    media: {
      poster: WPImage
      small_image: WPImage
      video: WPVideo
    }
    media_ratio: '1/1' | '454/480' | '900/952' | '640/800'
    alignment: 'left' | 'right'
    text_alignment: 'left' | 'center' | 'right'
    theme: WPPageData['acf']['theme']
  }>

const ImageParagraph = ({
  alignment = 'left',
  text_alignment = 'center',
  // media_ratio,
  media,
  paragraph,
  button,
  theme,
  ...props
}: ImageParagraphProps) => {
  const [ref, { height }] = useMeasure()
  return (
    <Motion animation={imageParagraph}>
      {(motionRef) => (
        <section
          ref={motionRef}
          {...props}
          className={cn([
            {
              'bg-background text-foreground': theme === 'light',
              'bg-black text-white': theme === 'dark'
            }
          ])}
        >
          <Container>
            <Grid className="gap-y-[6.4rem]">
              {paragraph ? (
                <div
                  className={cn([
                    'col-span-4 flex flex-col justify-center gap-[2.5rem]',
                    'md:col-span-5',
                    'md:gap-[5rem] lg:col-span-4',
                    {
                      'md:col-start-2 lg:col-start-2': alignment === 'left',
                      'md:col-start-7 lg:col-start-7': alignment === 'right',
                      'items-start text-left': text_alignment === 'left',
                      'items-center text-center': text_alignment === 'center',
                      'items-end text-right': text_alignment === 'right'
                    }
                  ])}
                >
                  {paragraph.title ? (
                    <Typography
                      variant="h1"
                      className="uppercase"
                      component="h2"
                      data-motion-title
                    >
                      {paragraph.title}
                    </Typography>
                  ) : null}
                  {paragraph.subtitle ? (
                    <Typography
                      variant="h3"
                      className="uppercase"
                      component="h3"
                      data-motion-subtitle
                    >
                      {paragraph.subtitle}
                    </Typography>
                  ) : null}
                  {paragraph.paragraph ? (
                    <Typography variant="body" data-motion-paragraph>
                      {paragraph.paragraph}
                    </Typography>
                  ) : null}
                  {paragraph.button && paragraph?.button?.url !== '#' ? (
                    <div data-motion-cta>
                      <Button
                        asChild
                        type="button"
                        variant={theme}
                        icon={
                          <SVGIcon
                            symbol="chevron-right-icon"
                            className="h-[11px] w-[7px] shrink-0 text-current"
                          />
                        }
                      >
                        <Link
                          href={paragraph.button.url}
                          target={paragraph.button.target}
                          data-label={paragraph.button.title}
                          scroll={false}
                        >
                          <Typography variant="label">
                            {paragraph.button.title}
                          </Typography>
                        </Link>
                      </Button>
                    </div>
                  ) : null}
                </div>
              ) : null}
              <div
                className={cn([
                  'col-span-4 flex items-center justify-center',
                  'md:col-span-4',
                  {
                    'md:col-start-8': alignment === 'left',
                    '-order-1 md:col-start-2': alignment === 'right'
                  }
                ])}
              >
                {button ? (
                  <div data-motion-cta>
                    <Button
                      asChild
                      type="button"
                      variant="light"
                      icon={
                        <SVGIcon
                          symbol="chevron-right-icon"
                          className="h-[11px] w-[7px] shrink-0 text-current"
                        />
                      }
                    >
                      <Link
                        href={button.url}
                        target={button.target}
                        data-label={button.title}
                        scroll={false}
                      >
                        <Typography variant="label">{button.title}</Typography>
                      </Link>
                    </Button>
                  </div>
                ) : null}
                <div className="aspect-square w-full">
                  {media?.poster?.url ? (
                    <MediaParallax className="h-full w-full">
                      {(ref: React.RefObject<HTMLElement>) =>
                        media.video?.url ? (
                          <video
                            ref={ref as React.RefObject<HTMLVideoElement>}
                            src={media.video.url.toString()}
                            poster={media.poster.url}
                            autoPlay
                            loop
                            muted
                            playsInline
                            className="h-full w-full object-cover"
                            data-motion-image
                          />
                        ) : (
                          <Image
                            ref={ref as React.RefObject<HTMLImageElement>}
                            source={media.poster}
                            size="full"
                            className={cn([
                              'h-full w-full object-cover'
                              // { 'object-contain': !media?.small_image?.url },
                              // { 'object-cover': media?.small_image?.url }
                            ])}
                            data-motion-image
                          />
                        )
                      }
                    </MediaParallax>
                  ) : null}
                </div>
                {media?.small_image?.url ? (
                  <div className="grid grid-cols-4 gap-x-gutter">
                    <div className="col-span-2 col-start-2">
                      <div
                        //@ts-ignore
                        ref={ref}
                      >
                        <MediaParallax
                          className="mx-auto aspect-square w-full"
                          style={{ marginTop: `${(height / 2) * -1}px` }}
                        >
                          {(ref: React.RefObject<HTMLElement>) => (
                            <Image
                              ref={ref as React.RefObject<HTMLImageElement>}
                              source={media.small_image}
                              size="full"
                              data-motion-secondary-image
                            />
                          )}
                        </MediaParallax>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            </Grid>
          </Container>
        </section>
      )}
    </Motion>
  )
}

export default ImageParagraph
